import { api } from './api';
import { DailyPlan, PlanDiscussion, TaskResult } from '../types/mundus';

export interface GeneratePlanInput {
  planDate: string; // ISO date string
}

export interface ApprovePlanInput {
  feedback?: string;
}

export interface RejectPlanInput {
  feedback: string;
}

export interface UpdateExecutionInput {
  actualResults: TaskResult[];
  completionRate?: number;
}

export interface AddDiscussionInput {
  content: string;
  messageType?: string;
}

/**
 * Generate a new daily plan
 */
export const generateDailyPlan = async (
  mundusId: string,
  planDate: Date
): Promise<DailyPlan> => {
  const response = await api.post(`/api/mundus/${mundusId}/plans/generate`, {
    planDate: planDate.toISOString(),
  });
  return response.data.data;
};

/**
 * Get daily plan for a specific date
 */
export const getDailyPlan = async (
  mundusId: string,
  date: Date
): Promise<DailyPlan | null> => {
  try {
    const response = await api.get(`/api/mundus/${mundusId}/plans`, {
      params: { date: date.toISOString() },
    });
    return response.data.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

/**
 * Get recent plans for a Mundus
 */
export const getRecentPlans = async (
  mundusId: string,
  limit: number = 7
): Promise<DailyPlan[]> => {
  const response = await api.get(`/api/mundus/${mundusId}/plans/recent`, {
    params: { limit },
  });
  return response.data.data;
};

/**
 * Approve a daily plan
 */
export const approveDailyPlan = async (
  planId: string,
  input: ApprovePlanInput
): Promise<DailyPlan> => {
  const response = await api.post(`/api/plans/${planId}/approve`, input);
  return response.data.data;
};

/**
 * Reject a daily plan
 */
export const rejectDailyPlan = async (
  planId: string,
  input: RejectPlanInput
): Promise<DailyPlan> => {
  const response = await api.post(`/api/plans/${planId}/reject`, input);
  return response.data.data;
};

/**
 * Update plan execution/progress
 */
export const updatePlanExecution = async (
  planId: string,
  input: UpdateExecutionInput
): Promise<DailyPlan> => {
  const response = await api.post(`/api/plans/${planId}/execution`, input);
  return response.data.data;
};

/**
 * Add a discussion message to a plan
 */
export const addPlanDiscussion = async (
  planId: string,
  input: AddDiscussionInput
): Promise<PlanDiscussion> => {
  const response = await api.post(`/api/plans/${planId}/discussions`, input);
  return response.data.data;
};

/**
 * Get plan discussions
 */
export const getPlanDiscussions = async (planId: string): Promise<PlanDiscussion[]> => {
  const response = await api.get(`/api/plans/${planId}/discussions`);
  return response.data.data;
};
